import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiUploadCloud,
  FiX,
  FiLink,
  FiFileText,
  FiLayers,
  FiMaximize2,
  FiPhone,
  FiMail,
  FiUser,
  FiInfo,
  FiCheckCircle,
  FiShield
} from 'react-icons/fi';
import API from '../api/axios';
import toast from 'react-hot-toast';
import './PrintOrderForm.css';

// ── Sooraj Print Substrate Rates Matrix (₹ / sq. inch) ─────────────────────
export const MATERIALS = [
  {
    id: 'uv_dtf',
    label: 'UV DTF Transfer',
    emoji: '✨',
    badge: 'Popular',
    baseRate: 1.80, // ₹1.80/sq.in (~₹260/sq.ft)
    description: 'High-adhesion peel & stick transfers for curved, cylindrical & irregular flat surfaces.'
  },
  {
    id: 'toughened_glass',
    label: 'Toughened Glass',
    emoji: '🛡️',
    badge: 'Premium',
    baseRate: 3.50, // ₹3.50/sq.in (~₹504/sq.ft)
    description: '5mm-8mm ultra-clear architectural tempered safety glass with reverse UV backing.'
  },
  {
    id: 'acrylic',
    label: 'Cast Acrylic',
    emoji: '💎',
    badge: 'Luxury',
    baseRate: 2.50, // ₹2.50/sq.in (~₹360/sq.ft)
    description: '3mm-5mm crystal clear cast acrylic sheet with diamond flame-polished edges.'
  },
  {
    id: 'metal',
    label: 'Metal / Aluminium',
    emoji: '🔩',
    badge: 'Durable',
    baseRate: 2.20, // ₹2.20/sq.in (~₹316/sq.ft)
    description: 'Brushed aluminium composite / metal plates with scratch & corrosion-resistant UV cure.'
  },
  {
    id: 'canvas',
    label: 'Cotton / Poly Canvas',
    emoji: '🖼️',
    badge: 'Artisan',
    baseRate: 1.20, // ₹1.20/sq.in (~₹172/sq.ft)
    description: '380 GSM archival textured cotton-polyester canvas with museum-grade color brilliance.'
  },
  {
    id: 'wallpaper',
    label: 'Custom Wallpaper',
    emoji: '🏠',
    badge: 'Eco-UV',
    baseRate: 0.75, // ₹0.75/sq.in (~₹108/sq.ft)
    description: 'Seamless textured non-woven interior wall coverings printed with odorless green inks.'
  },
  {
    id: 'acp_board',
    label: 'ACP Sign Board',
    emoji: '🪟',
    badge: 'Exterior',
    baseRate: 1.90, // ₹1.90/sq.in (~₹273/sq.ft)
    description: '3mm heavy-duty exterior weather-proof aluminium composite sign panels.'
  },
  {
    id: 'backlit_led',
    label: 'Backlit LED Panel',
    emoji: '💡',
    badge: 'High Glow',
    baseRate: 3.20, // ₹3.20/sq.in (~₹460/sq.ft)
    description: 'Ultra-vibrant light-diffusing acrylic film designed for LED illuminated lightboxes.'
  },
  {
    id: 'vinyl',
    label: 'Heavy-Duty Vinyl / Decal',
    emoji: '🏷️',
    badge: 'Weatherproof',
    baseRate: 0.85, // ₹0.85/sq.in (~₹122/sq.ft)
    description: 'Waterproof high-tack adhesive vinyl decal with UV cure for branding and equipment.'
  },
  {
    id: 'flex_banner',
    label: 'Outdoor Flex Banner',
    emoji: '🚩',
    badge: 'Outdoor Spec',
    baseRate: 0.45, // ₹0.45/sq.in (~₹65/sq.ft)
    description: 'High-tenacity frontlit / backlit flex banners for hoardings, events & shopfronts.'
  },
  {
    id: 'vinyl_banner',
    label: 'Heavy-Duty Vinyl Banner',
    emoji: '🏁',
    badge: 'Heavy Spec',
    baseRate: 0.50, // ₹0.50/sq.in (~₹72/sq.ft)
    description: 'Heavy-duty 440 GSM frontlit vinyl banner with high weather resistance.'
  },
  {
    id: 'pvc_foam_board',
    label: 'PVC Foam Board / Sunboard',
    emoji: '📋',
    badge: 'Rigid Sign',
    baseRate: 1.10, // ₹1.10/sq.in (~₹158/sq.ft)
    description: '3mm-5mm high density rigid PVC foam sheet for point-of-sale displays and indoor signage.'
  }
];

export const QUANTITY_OPTIONS = [1, 5, 10, 25, 50, 100];

export const FINISHING_OPTIONS = [
  {
    id: 'ultra_gloss',
    label: 'Ultra-Gloss Clear',
    multiplier: 1.0,
    tag: 'Standard',
    description: 'Reflective crystal glass mirror shine with full UV fade protection.'
  },
  {
    id: 'matte_satin',
    label: 'Matte Velvet Satin',
    multiplier: 1.08,
    tag: '+8%',
    description: 'Sophisticated anti-glare soft-touch velvet luxury aesthetic.'
  },
  {
    id: 'raised_varnish',
    label: '3D Raised Varnish',
    multiplier: 1.25,
    tag: '+25%',
    description: 'Tactile embossed textures & selective spot UV glossy depth.'
  },
  {
    id: 'high_density_white',
    label: 'High-Density White',
    multiplier: 1.15,
    tag: '+15%',
    description: 'Multi-layer opaque white underbase for deep contrast on transparent/dark materials.'
  },
  {
    id: 'grommets',
    label: 'Eyelets & Grommets',
    multiplier: 1.08,
    tag: '+8%',
    description: 'Reinforced brass eyelets along all edges and corners for hanging.'
  },
  {
    id: 'lamination',
    label: 'Protective Lamination',
    multiplier: 1.10,
    tag: '+10%',
    description: 'Weatherproof anti-scratch matte or gloss laminate.'
  },
  {
    id: 'grommets_lamination',
    label: 'Grommets & Lamination',
    multiplier: 1.18,
    tag: '+18%',
    description: 'Reinforced brass eyelets plus full protective laminate coat.'
  }
];

const PRESET_SIZES = [
  { label: '12" × 18" (A3+)', w: 12, h: 18, unit: 'in' },
  { label: '18" × 24" (Poster)', w: 18, h: 24, unit: 'in' },
  { label: '24" × 36" (Architectural)', w: 24, h: 36, unit: 'in' },
  { label: '36" × 48" (Large Format)', w: 36, h: 48, unit: 'in' }
];

const STEPS = ['Material', 'Dimensions', 'Qty & Finish', 'Artwork', 'Contact'];
const TOTAL_STEPS = STEPS.length;

const WHATSAPP_PHONE = '918369429165';

// ── Conversion & Math Helpers ──────────────────────────────────────────────
export function toInches(val, unit) {
  const v = parseFloat(val) || 0;
  if (unit === 'cm') return v / 2.54;
  if (unit === 'ft') return v * 12.0;
  return v;
}

export function fromInches(valInches, targetUnit) {
  const v = parseFloat(valInches) || 0;
  if (targetUnit === 'cm') return (v * 2.54).toFixed(1);
  if (targetUnit === 'ft') return (v / 12.0).toFixed(2);
  return v.toFixed(1);
}

export function getVolumeDiscountRate(qty) {
  const q = parseInt(qty, 10) || 1;
  if (q >= 100) return 0.25; // 25% discount
  if (q >= 50) return 0.20;  // 20% discount
  if (q >= 25) return 0.15;  // 15% discount
  if (q >= 10) return 0.10;  // 10% discount
  if (q >= 5) return 0.05;   // 5% discount
  return 0.0;
}

export const MIN_ORDER_VALUE = 50;

export function calcEstimate({ material, width, height, unit = 'in', quantity = 1, finishing = 'ultra_gloss' }) {
  const mat = MATERIALS.find(m => m.id === material);
  const fin = FINISHING_OPTIONS.find(f => f.id === finishing) || FINISHING_OPTIONS[0];
  const wIn = toInches(width, unit);
  const hIn = toInches(height, unit);

  if (!mat || wIn <= 0 || hIn <= 0) return null;

  const areaSqIn = wIn * hIn;
  const areaSqFt = areaSqIn / 144.0;
  const areaSqCm = areaSqIn * 6.4516;

  const baseUnitCost = areaSqIn * mat.baseRate;
  const withFinishing = baseUnitCost * fin.multiplier;
  const qty = Math.max(1, parseInt(quantity, 10) || 1);
  const discountRate = getVolumeDiscountRate(qty);
  const discountedUnitPrice = Math.max(1, Math.round(withFinishing * (1 - discountRate)));
  const rawTotal = discountedUnitPrice * qty;
  const total = Math.max(MIN_ORDER_VALUE, rawTotal);

  return {
    unitPrice: discountedUnitPrice,
    rawTotal,
    total,
    isMinOrderApplied: rawTotal < MIN_ORDER_VALUE,
    minOrderValue: MIN_ORDER_VALUE,
    areaSqIn: Math.round(areaSqIn * 10) / 10,
    areaSqFt: (Math.round(areaSqFt * 100) / 100).toFixed(2),
    areaSqCm: Math.round(areaSqCm),
    discountPercent: Math.round(discountRate * 100),
    materialName: mat.label,
    finishingName: fin.label
  };
}

// ── Inline WhatsApp Icon ───────────────────────────────────────────────────
function WhatsAppIcon({ size = 18, color = '#25D366' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', flexShrink: 0 }}>
      <path
        d="M20.52 3.48A11.9 11.9 0 0012.06 0C5.45 0 .09 5.36.09 11.97c0 2.11.55 4.17 1.6 5.98L0 24l6.23-1.63a11.93 11.93 0 005.83 1.51h.01c6.61 0 11.97-5.36 11.97-11.97 0-3.2-1.25-6.21-3.52-8.43zM12.07 21.86h-.01a9.88 9.88 0 01-5.04-1.38l-.36-.21-3.75.98 1-3.65-.23-.38a9.88 9.88 0 01-1.52-5.25c0-5.46 4.45-9.91 9.92-9.91 2.65 0 5.14 1.03 7.01 2.9a9.85 9.85 0 012.91 7.01c0 5.46-4.45 9.89-9.93 9.89zm5.44-7.42c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.8.37-.27.3-1.05 1.03-1.05 2.51s1.07 2.91 1.22 3.11c.15.2 2.11 3.22 5.11 4.52.71.31 1.27.5 1.7.64.72.23 1.37.2 1.89.12.58-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z"
        fill={color}
      />
    </svg>
  );
}

// ── Step Indicator ──────────────────────────────────────────────────────────
function StepIndicator({ current }) {
  return (
    <div className="pof-step-indicator">
      {STEPS.map((label, idx) => (
        <React.Fragment key={idx}>
          <div className={`pof-step-node ${idx < current ? 'completed' : idx === current ? 'active' : ''}`}>
            <div className="pof-step-circle">
              {idx < current ? <FiCheck size={13} /> : idx + 1}
            </div>
            <span className="pof-step-name">{label}</span>
          </div>
          {idx < STEPS.length - 1 && (
            <div className={`pof-step-connector ${idx < current ? 'completed' : ''}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Live Estimator Floating Bar ─────────────────────────────────────────────
function LivePriceCard({ estimate, onQuickWhatsApp }) {
  if (!estimate) {
    return (
      <div className="pof-estimate-bar pof-estimate-bar--empty">
        <div className="pof-est-info">
          <span className="pof-est-hint">✨ Select material and dimensions to see instant estimate</span>
        </div>
      </div>
    );
  }

  return (
    <div className="pof-estimate-bar">
      <div className="pof-est-primary">
        <span className="pof-est-label">Estimated Quotation</span>
        <div className="pof-est-price-wrap">
          <span className="pof-est-currency">₹</span>
          <span className="pof-est-amount">{estimate.total.toLocaleString('en-IN')}</span>
          <span className="pof-est-unit">({estimate.unitPrice.toLocaleString('en-IN')} / pc)</span>
        </div>
      </div>
      <div className="pof-est-meta">
        <span className="pof-est-chip">{estimate.areaSqFt} sq.ft</span>
        {estimate.discountPercent > 0 && (
          <span className="pof-est-discount">-{estimate.discountPercent}% Bulk Off</span>
        )}
        {estimate.isMinOrderApplied && (
          <span className="pof-est-discount" style={{ background: 'rgba(255, 183, 77, 0.2)', color: '#FFB74D' }}>Min. Order ₹50</span>
        )}
      </div>
      {onQuickWhatsApp && (
        <button
          type="button"
          className="pof-est-wa-btn"
          onClick={onQuickWhatsApp}
          title="Send Quote via WhatsApp"
        >
          <WhatsAppIcon size={16} color="#ffffff" />
          <span>WhatsApp</span>
        </button>
      )}
    </div>
  );
}

// ── Main PrintOrderForm Component ───────────────────────────────────────────
const PrintOrderForm = ({ onClose, initialMaterial }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);

  // Form State
  const [form, setForm] = useState({
    material: initialMaterial || 'uv_dtf',
    unit: 'in', // 'in' | 'cm' | 'ft'
    width: '12',
    height: '18',
    quantity: 1,
    customQuantity: '',
    isCustomQty: false,
    finishing: 'ultra_gloss',
    artworkMode: 'file', // 'file' | 'link'
    artworkFile: null,
    artworkFilePreview: null,
    driveLink: '',
    artworkNotes: '',
    name: '',
    phone: '',
    email: '',
    notes: ''
  });

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // Derive active quantity
  const activeQty = form.isCustomQty
    ? Math.max(1, parseInt(form.customQuantity, 10) || 1)
    : form.quantity;

  // Real-time calculated estimate
  const estimate = calcEstimate({
    material: form.material,
    width: form.width,
    height: form.height,
    unit: form.unit,
    quantity: activeQty,
    finishing: form.finishing
  });

  // Handle unit switching with numerical conversion to preserve size
  const handleUnitChange = (newUnit) => {
    if (newUnit === form.unit) return;
    const currentWInches = toInches(form.width, form.unit);
    const currentHInches = toInches(form.height, form.unit);
    const convertedW = fromInches(currentWInches, newUnit);
    const convertedH = fromInches(currentHInches, newUnit);

    setForm(prev => ({
      ...prev,
      unit: newUnit,
      width: convertedW,
      height: convertedH
    }));
  };

  // Preset size selector
  const applyPresetSize = (preset) => {
    setForm(prev => ({
      ...prev,
      unit: preset.unit,
      width: String(preset.w),
      height: String(preset.h)
    }));
  };

  // Step Validation
  const canProceed = useCallback(() => {
    switch (step) {
      case 0:
        return !!form.material;
      case 1:
        return parseFloat(form.width) > 0 && parseFloat(form.height) > 0;
      case 2:
        return !!form.finishing && activeQty >= 1;
      case 3:
        return true; // Artwork optional (can share via WhatsApp)
      case 4:
        return (
          form.name.trim().length >= 2 &&
          /^[6-9]\d{9}$/.test(form.phone.trim().replace(/\D/g, ''))
        );
      default:
        return false;
    }
  }, [step, form, activeQty]);

  const handleNext = () => {
    if (canProceed()) {
      setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
    }
  };

  const handlePrev = () => {
    setStep(s => Math.max(s - 1, 0));
  };

  // File Upload Handlers
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error('File exceeds 50MB. Please use Google Drive link instead.');
      return;
    }

    setField('artworkFile', file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setField('artworkFilePreview', reader.result);
      reader.readAsDataURL(file);
    } else {
      setField('artworkFilePreview', null);
    }
  };

  const removeFile = () => {
    setField('artworkFile', null);
    setField('artworkFilePreview', null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Format WhatsApp message
  const generateWhatsAppMessage = (quoteId = null) => {
    const selectedMat = MATERIALS.find(m => m.id === form.material);
    const selectedFin = FINISHING_OPTIONS.find(f => f.id === form.finishing);
    const est = estimate || {};

    let msg = `*NCR PRINTS / SHUBHAM COMPUTERS QUOTATION INQUIRY*\n`;
    if (quoteId) {
      msg += `🆔 *Quote Reference:* ${quoteId}\n`;
    }
    msg += `----------------------------------------\n`;
    msg += `📦 *Material:* ${selectedMat?.label || form.material}\n`;
    msg += `📐 *Dimensions:* ${form.width} × ${form.height} ${form.unit.toUpperCase()} (${est.areaSqFt || '0'} sq.ft / ${est.areaSqIn || '0'} sq.in)\n`;
    msg += `🔢 *Quantity:* ${activeQty} pc(s)\n`;
    msg += `✨ *Finishing:* ${selectedFin?.label || form.finishing}\n`;
    msg += `💰 *Estimated Price:* ₹${est.total ? est.total.toLocaleString('en-IN') : 'TBD'} (₹${est.unitPrice ? est.unitPrice.toLocaleString('en-IN') : '0'}/pc)\n`;

    if (form.artworkMode === 'link' && form.driveLink.trim()) {
      msg += `🔗 *Drive/Cloud Artwork:* ${form.driveLink.trim()}\n`;
    } else if (form.artworkFile) {
      msg += `📁 *Artwork File:* ${form.artworkFile.name} (${(form.artworkFile.size / 1024).toFixed(0)} KB)\n`;
    } else {
      msg += `📁 *Artwork:* Sharing directly via WhatsApp chat\n`;
    }

    if (form.artworkNotes?.trim()) {
      msg += `📝 *Production Notes:* ${form.artworkNotes.trim()}\n`;
    }

    msg += `----------------------------------------\n`;
    msg += `👤 *Customer Details:*\n`;
    msg += `• *Name:* ${form.name.trim() || 'Valued Customer'}\n`;
    msg += `• *Phone:* ${form.phone.trim() || 'N/A'}\n`;
    if (form.email?.trim()) {
      msg += `• *Email:* ${form.email.trim()}\n`;
    }
    if (form.notes?.trim()) {
      msg += `• *Delivery Notes:* ${form.notes.trim()}\n`;
    }
    msg += `\nPlease confirm production turnaround time and payment options!`;

    return msg;
  };

  const handleSendWhatsApp = (quoteId = null) => {
    const message = generateWhatsAppMessage(quoteId);
    const url = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Submit Quote to Express backend
  const handleSubmitQuote = async () => {
    if (!canProceed()) return;
    setSubmitting(true);

    try {
      let responseData;
      if (form.artworkFile) {
        const formData = new FormData();
        formData.append('material', form.material);
        formData.append('width', form.width);
        formData.append('height', form.height);
        formData.append('unit', form.unit);
        formData.append('quantity', activeQty);
        formData.append('finishing', form.finishing);
        formData.append('name', form.name.trim());
        formData.append('phone', form.phone.trim());
        formData.append('email', form.email.trim());
        formData.append('notes', [form.artworkNotes, form.notes].filter(Boolean).join(' - '));
        if (form.driveLink.trim()) formData.append('drive_link', form.driveLink.trim());
        formData.append('artworkFile', form.artworkFile);

        const res = await API.post('/quote', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        responseData = res.data;
      } else {
        const payload = {
          material: form.material,
          width: form.width,
          height: form.height,
          unit: form.unit,
          quantity: activeQty,
          finishing: form.finishing,
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          notes: [form.artworkNotes, form.notes].filter(Boolean).join(' - '),
          drive_link: form.driveLink.trim()
        };
        const res = await API.post('/quote', payload);
        responseData = res.data;
      }

      setQuoteResult(responseData);
      setSubmitted(true);
      toast.success('Quotation request saved successfully!');
    } catch (err) {
      console.error('Quote submission error:', err);
      const errorMsg = err?.response?.data?.error || 'Failed to submit quote. You can still send via WhatsApp!';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success Confirmation Screen ───────────────────────────────────────────
  if (submitted && quoteResult) {
    return (
      <div className="pof-modal-overlay" onClick={onClose}>
        <div className="pof-modal-card pof-success-card" onClick={e => e.stopPropagation()}>
          <div className="pof-success-header">
            <div className="pof-success-icon-badge">
              <FiCheckCircle size={38} color="#00D9A6" />
            </div>
            <h2 className="pof-success-title">Quotation Request Received!</h2>
            <p className="pof-success-sub">
              Your official estimate has been recorded. Our print technician will review your specifications.
            </p>
          </div>

          <div className="pof-quote-summary-box">
            <div className="pof-summary-row">
              <span className="pof-summary-lbl">Quote ID</span>
              <strong className="pof-summary-val pof-code-val">{quoteResult.quote_id}</strong>
            </div>
            <div className="pof-summary-row">
              <span className="pof-summary-lbl">Substrate</span>
              <span className="pof-summary-val">{estimate?.materialName || form.material}</span>
            </div>
            <div className="pof-summary-row">
              <span className="pof-summary-lbl">Dimensions</span>
              <span className="pof-summary-val">
                {form.width} × {form.height} {form.unit.toUpperCase()} ({estimate?.areaSqFt} sq.ft)
              </span>
            </div>
            <div className="pof-summary-row">
              <span className="pof-summary-lbl">Quantity & Finish</span>
              <span className="pof-summary-val">{activeQty} pcs • {estimate?.finishingName}</span>
            </div>
            <div className="pof-summary-divider" />
            <div className="pof-summary-row pof-summary-highlight">
              <span className="pof-summary-lbl">Estimated Price</span>
              <strong className="pof-summary-price">
                ₹{(quoteResult.estimated_price || estimate?.total || 0).toLocaleString('en-IN')}
              </strong>
            </div>
          </div>

          <div className="pof-success-actions">
            <button
              type="button"
              className="pof-btn pof-btn--whatsapp pof-btn--full"
              onClick={() => handleSendWhatsApp(quoteResult.quote_id)}
            >
              <WhatsAppIcon size={20} color="#ffffff" />
              <span>Send Quote to WhatsApp (+91 83694 29165)</span>
            </button>
            <button
              type="button"
              className="pof-btn pof-btn--secondary pof-btn--full"
              onClick={() => {
                if (onClose) onClose();
                else navigate('/');
              }}
            >
              Done & Return
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Multi-Step Form UI ────────────────────────────────────────────────────
  return (
    <div className="pof-modal-overlay" onClick={onClose}>
      <div className="pof-modal-card" onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="pof-header">
          <div className="pof-header-left">
            {step > 0 ? (
              <button type="button" className="pof-icon-btn" onClick={handlePrev} title="Previous Step">
                <FiArrowLeft size={18} />
              </button>
            ) : (
              <div className="pof-header-logo-badge">
                <FiLayers size={18} color="#FFB74D" />
              </div>
            )}
            <div>
              <h3 className="pof-header-title">NCR Prints Quotation Studio</h3>
              <span className="pof-header-subtitle">UV Flatbed, Acrylic, Glass & Custom Substrates</span>
            </div>
          </div>

          <div className="pof-header-right">
            <span className="pof-step-count">
              Step {step + 1} of {TOTAL_STEPS}
            </span>
            {onClose && (
              <button type="button" className="pof-icon-btn pof-close-btn" onClick={onClose} title="Close">
                <FiX size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Multi-step progress bar */}
        <StepIndicator current={step} />

        {/* Live Estimator Bar */}
        <LivePriceCard
          estimate={estimate}
          onQuickWhatsApp={() => handleSendWhatsApp(null)}
        />

        {/* Step Content */}
        <div className="pof-body">
          {/* ═════════ STEP 1: MATERIAL SELECTION ═════════ */}
          {step === 0 && (
            <div className="pof-step-pane">
              <div className="pof-pane-heading">
                <h4>Choose Premium Print Substrate</h4>
                <p>Select the material for your high-precision UV or specialty print.</p>
              </div>

              <div className="pof-materials-grid">
                {MATERIALS.map(item => {
                  const isSelected = form.material === item.id;
                  return (
                    <div
                      key={item.id}
                      className={`pof-mat-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => setField('material', item.id)}
                    >
                      <div className="pof-mat-top">
                        <span className="pof-mat-emoji">{item.emoji}</span>
                        <span className="pof-mat-badge">{item.badge}</span>
                      </div>
                      <h5 className="pof-mat-title">{item.label}</h5>
                      <p className="pof-mat-desc">{item.description}</p>
                      <div className="pof-mat-footer">
                        <span className="pof-mat-rate">
                          ₹{item.baseRate.toFixed(2)}
                          <small>/sq.in</small>
                        </span>
                        <span className="pof-mat-sqft">
                          (~₹{Math.round(item.baseRate * 144)}/sq.ft)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═════════ STEP 2: DIMENSIONS ═════════ */}
          {step === 1 && (
            <div className="pof-step-pane">
              <div className="pof-pane-heading">
                <h4>Enter Print Dimensions</h4>
                <p>Select your preferred unit and specify exact width and height.</p>
              </div>

              {/* Unit Toggle */}
              <div className="pof-unit-switch-wrap">
                <label className="pof-label">Measurement Unit</label>
                <div className="pof-unit-switch">
                  {[
                    { id: 'in', label: 'Inches (in)' },
                    { id: 'cm', label: 'Centimetres (cm)' },
                    { id: 'ft', label: 'Feet (ft)' }
                  ].map(u => (
                    <button
                      key={u.id}
                      type="button"
                      className={`pof-unit-btn ${form.unit === u.id ? 'active' : ''}`}
                      onClick={() => handleUnitChange(u.id)}
                    >
                      {u.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dimension Inputs */}
              <div className="pof-dim-inputs-grid">
                <div className="pof-input-group">
                  <label className="pof-label">Width ({form.unit})</label>
                  <div className="pof-dim-input-wrapper">
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      className="pof-text-input"
                      placeholder={`e.g. ${form.unit === 'ft' ? '2' : form.unit === 'cm' ? '60' : '24'}`}
                      value={form.width}
                      onChange={e => setField('width', e.target.value)}
                    />
                    <span className="pof-dim-suffix">{form.unit}</span>
                  </div>
                </div>

                <div className="pof-dim-sep">×</div>

                <div className="pof-input-group">
                  <label className="pof-label">Height ({form.unit})</label>
                  <div className="pof-dim-input-wrapper">
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      className="pof-text-input"
                      placeholder={`e.g. ${form.unit === 'ft' ? '3' : form.unit === 'cm' ? '90' : '36'}`}
                      value={form.height}
                      onChange={e => setField('height', e.target.value)}
                    />
                    <span className="pof-dim-suffix">{form.unit}</span>
                  </div>
                </div>
              </div>

              {/* Quick Presets */}
              <div className="pof-presets-block">
                <label className="pof-label">Standard Popular Presets</label>
                <div className="pof-presets-list">
                  {PRESET_SIZES.map(p => (
                    <button
                      key={p.label}
                      type="button"
                      className="pof-preset-chip"
                      onClick={() => applyPresetSize(p)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Area Calculation Breakdown Card */}
              {estimate && (
                <div className="pof-area-card">
                  <div className="pof-area-item">
                    <span className="pof-area-lbl">Square Feet</span>
                    <strong className="pof-area-val">{estimate.areaSqFt} sq.ft</strong>
                  </div>
                  <div className="pof-area-divider" />
                  <div className="pof-area-item">
                    <span className="pof-area-lbl">Square Inches</span>
                    <strong className="pof-area-val">{estimate.areaSqIn} sq.in</strong>
                  </div>
                  <div className="pof-area-divider" />
                  <div className="pof-area-item">
                    <span className="pof-area-lbl">Square Centimetres</span>
                    <strong className="pof-area-val">{estimate.areaSqCm} cm²</strong>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═════════ STEP 3: QUANTITY & FINISHING ═════════ */}
          {step === 2 && (
            <div className="pof-step-pane">
              <div className="pof-pane-heading">
                <h4>Quantity & Surface Finishing</h4>
                <p>Unlock bulk tier discounts and pick protective UV coating.</p>
              </div>

              {/* Quantity Picker */}
              <div className="pof-qty-section">
                <div className="pof-qty-header">
                  <label className="pof-label">Select Quantity</label>
                  <span className="pof-bulk-tag">
                    {activeQty >= 100
                      ? '⚡ 25% Wholesale Discount Active'
                      : activeQty >= 10
                      ? '⚡ Tier Discount Active'
                      : 'Tier discounts up to 25% off on bulk'}
                  </span>
                </div>

                <div className="pof-qty-buttons">
                  {QUANTITY_OPTIONS.map(q => (
                    <button
                      key={q}
                      type="button"
                      className={`pof-qty-btn ${!form.isCustomQty && form.quantity === q ? 'active' : ''}`}
                      onClick={() => {
                        setField('isCustomQty', false);
                        setField('quantity', q);
                      }}
                    >
                      {q}
                      {q >= 10 && <span className="pof-qty-off">-{getVolumeDiscountRate(q) * 100}%</span>}
                    </button>
                  ))}

                  <button
                    type="button"
                    className={`pof-qty-btn pof-qty-btn--custom ${form.isCustomQty ? 'active' : ''}`}
                    onClick={() => setField('isCustomQty', true)}
                  >
                    Custom +
                  </button>
                </div>

                {form.isCustomQty && (
                  <div className="pof-custom-qty-box">
                    <input
                      type="number"
                      min="1"
                      className="pof-text-input"
                      placeholder="Enter custom quantity (e.g. 150)"
                      value={form.customQuantity}
                      onChange={e => setField('customQuantity', e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Finishing Options */}
              <div className="pof-finishing-section">
                <label className="pof-label">Premium Finishing & Protection</label>
                <div className="pof-finishing-grid">
                  {FINISHING_OPTIONS.map(fin => {
                    const isSelected = form.finishing === fin.id;
                    return (
                      <div
                        key={fin.id}
                        className={`pof-fin-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => setField('finishing', fin.id)}
                      >
                        <div className="pof-fin-top">
                          <span className="pof-fin-name">{fin.label}</span>
                          <span className="pof-fin-tag">{fin.tag}</span>
                        </div>
                        <p className="pof-fin-desc">{fin.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ═════════ STEP 4: ARTWORK / FILE DETAILS ═════════ */}
          {step === 3 && (
            <div className="pof-step-pane">
              <div className="pof-pane-heading">
                <h4>Artwork & Design Files</h4>
                <p>Provide your high-resolution vector or raster artwork for print preparation.</p>
              </div>

              {/* Artwork Mode Switch */}
              <div className="pof-tab-switch">
                <button
                  type="button"
                  className={`pof-tab-btn ${form.artworkMode === 'file' ? 'active' : ''}`}
                  onClick={() => setField('artworkMode', 'file')}
                >
                  <FiUploadCloud size={16} />
                  <span>Direct Upload</span>
                </button>
                <button
                  type="button"
                  className={`pof-tab-btn ${form.artworkMode === 'link' ? 'active' : ''}`}
                  onClick={() => setField('artworkMode', 'link')}
                >
                  <FiLink size={16} />
                  <span>Google Drive / Cloud Link</span>
                </button>
              </div>

              {/* File Upload Zone */}
              {form.artworkMode === 'file' && (
                <div className="pof-upload-container">
                  {!form.artworkFile ? (
                    <div
                      className="pof-dropzone"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FiUploadCloud size={36} className="pof-drop-icon" />
                      <span className="pof-drop-main">Click or drop print file here</span>
                      <span className="pof-drop-sub">
                        PDF, PNG, JPG, TIFF, PSD, CDR, AI (Max 50MB)
                      </span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf,.psd,.ai,.cdr,.tiff"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                      />
                    </div>
                  ) : (
                    <div className="pof-file-preview-card">
                      {form.artworkFilePreview ? (
                        <img
                          src={form.artworkFilePreview}
                          alt="Artwork Preview"
                          className="pof-file-thumb"
                        />
                      ) : (
                        <div className="pof-file-thumb-doc">
                          <FiFileText size={28} />
                        </div>
                      )}
                      <div className="pof-file-meta">
                        <strong className="pof-file-name">{form.artworkFile.name}</strong>
                        <span className="pof-file-size">
                          {(form.artworkFile.size / (1024 * 1024)).toFixed(2)} MB • Ready
                        </span>
                      </div>
                      <button
                        type="button"
                        className="pof-file-remove"
                        onClick={removeFile}
                        title="Remove file"
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Drive Link Input */}
              {form.artworkMode === 'link' && (
                <div className="pof-link-container">
                  <label className="pof-label">Google Drive / Dropbox / WeTransfer URL</label>
                  <div className="pof-input-with-icon">
                    <FiLink className="pof-input-icon" />
                    <input
                      type="url"
                      className="pof-text-input pof-text-input--has-icon"
                      placeholder="https://drive.google.com/file/d/.../view?usp=sharing"
                      value={form.driveLink}
                      onChange={e => setField('driveLink', e.target.value)}
                    />
                  </div>
                  <span className="pof-helper-note">
                    🔒 Ensure access is set to "Anyone with the link can view".
                  </span>
                </div>
              )}

              {/* Artwork Instructions / Notes */}
              <div className="pof-input-group" style={{ marginTop: '1.2rem' }}>
                <label className="pof-label">Artwork Specifications / Instructions (Optional)</label>
                <textarea
                  rows={3}
                  className="pof-textarea"
                  placeholder="Specify margin bleed, CMYK profile, contour cut lines, or special alignment details..."
                  value={form.artworkNotes}
                  onChange={e => setField('artworkNotes', e.target.value)}
                />
              </div>

              <div className="pof-skip-row">
                <button
                  type="button"
                  className="pof-skip-btn"
                  onClick={handleNext}
                >
                  I'll send artwork later via WhatsApp ➔
                </button>
              </div>
            </div>
          )}

          {/* ═════════ STEP 5: CONTACT & FINAL REVIEW ═════════ */}
          {step === 4 && (
            <div className="pof-step-pane">
              <div className="pof-pane-heading">
                <h4>Contact & Dispatch Details</h4>
                <p>Provide contact details for formal quotation delivery and follow-up.</p>
              </div>

              <div className="pof-contact-grid">
                <div className="pof-input-group">
                  <label className="pof-label">Full Name *</label>
                  <div className="pof-input-with-icon">
                    <FiUser className="pof-input-icon" />
                    <input
                      type="text"
                      className="pof-text-input pof-text-input--has-icon"
                      placeholder="e.g. Rahul Sharma"
                      value={form.name}
                      onChange={e => setField('name', e.target.value)}
                    />
                  </div>
                </div>

                <div className="pof-input-group">
                  <label className="pof-label">WhatsApp Mobile Number *</label>
                  <div className="pof-input-with-icon">
                    <span className="pof-phone-prefix">+91</span>
                    <input
                      type="tel"
                      maxLength={10}
                      className="pof-text-input pof-text-input--phone"
                      placeholder="10-digit mobile"
                      value={form.phone}
                      onChange={e => setField('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    />
                  </div>
                </div>

                <div className="pof-input-group">
                  <label className="pof-label">Email Address (Optional)</label>
                  <div className="pof-input-with-icon">
                    <FiMail className="pof-input-icon" />
                    <input
                      type="email"
                      className="pof-text-input pof-text-input--has-icon"
                      placeholder="e.g. rahul@example.com"
                      value={form.email}
                      onChange={e => setField('email', e.target.value)}
                    />
                  </div>
                </div>

                <div className="pof-input-group">
                  <label className="pof-label">Delivery Address / Project Notes</label>
                  <textarea
                    rows={2}
                    className="pof-textarea"
                    placeholder="Enter delivery area in Jhajjar NCR or shipping destination, timeline requirements..."
                    value={form.notes}
                    onChange={e => setField('notes', e.target.value)}
                  />
                </div>
              </div>

              {/* Service Assurance Badges */}
              <div className="pof-assurance-row">
                <div className="pof-assure-item">
                  <FiShield size={16} color="#00D9A6" />
                  <span>Jhajjar 30-min Express / NCR Shipping</span>
                </div>
                <div className="pof-assure-item">
                  <FiCheckCircle size={16} color="#6C63FF" />
                  <span>Commercial High-Precision UV Flatbed</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Controls */}
        <div className="pof-footer">
          {step > 0 && (
            <button
              type="button"
              className="pof-btn pof-btn--back"
              onClick={handlePrev}
            >
              <FiArrowLeft size={16} />
              <span>Back</span>
            </button>
          )}

          {step < TOTAL_STEPS - 1 ? (
            <button
              type="button"
              className={`pof-btn pof-btn--primary ${!canProceed() ? 'disabled' : ''}`}
              onClick={handleNext}
              disabled={!canProceed()}
            >
              <span>Continue</span>
              <FiArrowRight size={16} />
            </button>
          ) : (
            <div className="pof-final-buttons">
              <button
                type="button"
                className="pof-btn pof-btn--whatsapp"
                onClick={() => handleSendWhatsApp(null)}
              >
                <WhatsAppIcon size={18} color="#ffffff" />
                <span>Send Quote via WhatsApp</span>
              </button>

              <button
                type="button"
                className={`pof-btn pof-btn--submit ${submitting || !canProceed() ? 'disabled' : ''}`}
                onClick={handleSubmitQuote}
                disabled={submitting || !canProceed()}
              >
                {submitting ? 'Generating…' : 'Submit Quote Online'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintOrderForm;
